import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "../utils/axiosInstance";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Badge,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = "/message";
const API_URL1 = "/message_reciever";
const API_URL2 = "/message_mudugudu";

// ═══════════════════════════════════════════════════
// Custom Responsive Hook - Detects ALL devices
// ═══════════════════════════════════════════════════
const useResponsive = () => {
  const [screen, setScreen] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    const h = typeof window !== "undefined" ? window.innerHeight : 768;
    return {
      width: w,
      height: h,
      // Device categories
      isFoldable: w <= 320,          // Samsung Galaxy Fold (folded)
      isSmallMobile: w > 320 && w < 360,  // iPhone SE, small Androids
      isMediumMobile: w >= 360 && w < 414, // iPhone X/11/12/13/14, most Androids
      isLargeMobile: w >= 414 && w < 768,  // iPhone Plus/Max, large Androids
      isMobile: w < 768,
      isTabletPortrait: w >= 768 && w < 1024,
      isTabletLandscape: w >= 1024 && w < 1200,
      isTablet: w >= 768 && w < 1200,
      isDesktop: w >= 1200,
      isLargeDesktop: w >= 1400,
      isUltraWide: w >= 1920,
      // Orientation
      orientation: h > w ? "portrait" : "landscape",
      isLandscape: w > h,
      // Platform detection
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
      isAndroid: /Android/.test(navigator.userAgent),
      isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
      // Pixel ratio for retina
      pixelRatio: window.devicePixelRatio || 1,
      isRetina: window.devicePixelRatio > 1,
      // Touch capability
      hasTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    };
  });

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setScreen({
        width: w,
        height: h,
        isFoldable: w <= 320,
        isSmallMobile: w > 320 && w < 360,
        isMediumMobile: w >= 360 && w < 414,
        isLargeMobile: w >= 414 && w < 768,
        isMobile: w < 768,
        isTabletPortrait: w >= 768 && w < 1024,
        isTabletLandscape: w >= 1024 && w < 1200,
        isTablet: w >= 768 && w < 1200,
        isDesktop: w >= 1200,
        isLargeDesktop: w >= 1400,
        isUltraWide: w >= 1920,
        orientation: h > w ? "portrait" : "landscape",
        isLandscape: w > h,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isAndroid: /Android/.test(navigator.userAgent),
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        pixelRatio: window.devicePixelRatio || 1,
        isRetina: window.devicePixelRatio > 1,
        hasTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      });
    };

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", () => setTimeout(update, 150));

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", update);
    }

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", update);
      }
    };
  }, []);

  return screen;
};

// ═══════════════════════════════════════════════════
// Global Responsive Styles
// ═══════════════════════════════════════════════════
const ResponsiveStyles = ({ theme }) => (
  <style>{`
    /* ═══ CSS Variables & Safe Areas ═══ */
    :root {
      --sat: env(safe-area-inset-top, 0px);
      --sar: env(safe-area-inset-right, 0px);
      --sab: env(safe-area-inset-bottom, 0px);
      --sal: env(safe-area-inset-left, 0px);
    }

    /* ═══ Base Reset ═══ */
    * {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
    }

    html {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }

    body {
      
      -webkit-overflow-scrolling: touch;
    }

    /* ═══ Message Page Container ═══ */
    .msg-page {
      background: ${theme.bg};
      color: ${theme.text};
      min-height: 100vh;
      min-height: 100dvh;
    }

    /* ═══ Animations ═══ */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }

    .msg-bubble-anim {
      animation: fadeInUp 0.35s ease-out;
    }

    .msg-alert-anim {
      animation: slideDown 0.3s ease-out;
    }

    /* ═══ Scrollbar ═══ */
    .msg-custom-scroll::-webkit-scrollbar { width: 5px; }
    .msg-custom-scroll::-webkit-scrollbar-track { background: transparent; }
    .msg-custom-scroll::-webkit-scrollbar-thumb {
      background: ${theme.muted};
      border-radius: 10px;
    }
    .msg-custom-scroll::-webkit-scrollbar-thumb:hover {
      background: ${theme.primary};
    }

    /* ═══ Input iOS zoom prevention ═══ */
    .msg-input-fix {
      font-size: 16px !important;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    /* ═══ Touch-friendly ═══ */
    .msg-touch-target {
      min-height: 44px;
      min-width: 44px;
      cursor: pointer;
      -webkit-user-select: none;
      user-select: none;
    }

    /* ═══ Card Base ═══ */
    .msg-card {
      border: 1px solid ${theme.border} !important;
      transition: all 0.25s ease;
    }

    /* ═══ Form Focus States ═══ */
    .msg-page .form-control:focus,
    .msg-page .form-select:focus {
      border-color: ${theme.primary} !important;
      box-shadow: 0 0 0 0.25rem rgba(99,102,241,0.25) !important;
    }

    /* ═══ Mobile View Toggle ═══ */
    .msg-mobile-compose,
    .msg-mobile-feed {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* ═════════════════════════════════════════
       RESPONSIVE BREAKPOINTS
       ═════════════════════════════════════════ */

    /* ── Foldable (≤320px) - Galaxy Fold folded ── */
    @media (max-width: 320px) {
      .msg-page {
        padding: 8px !important;
      }
      .msg-page-header h2 {
        font-size: 1rem !important;
      }
      .msg-page-header small {
        font-size: 0.65rem !important;
      }
      .msg-compose-card .card-body {
        padding: 12px !important;
      }
      .msg-compose-card textarea {
        rows: 3;
        min-height: 60px !important;
      }
      .msg-emoji-sidebar {
        display: none !important;
      }
      .msg-bubble-content {
        max-width: 92% !important;
        padding: 8px 10px !important;
      }
      .msg-bubble-avatar {
        width: 24px !important;
        height: 24px !important;
        font-size: 0.7rem !important;
      }
      .msg-bubble-text {
        font-size: 0.8rem !important;
      }
      .msg-filter-input {
        max-width: 120px !important;
      }
      .msg-feed-scroll {
        max-height: 45vh !important;
      }
      .msg-submit-btn {
        padding: 10px !important;
        font-size: 0.85rem !important;
      }
    }

    /* ── Small Mobile (321-359px) - iPhone SE ── */
    @media (min-width: 321px) and (max-width: 359px) {
      .msg-page {
        padding: 10px !important;
      }
      .msg-page-header h2 {
        font-size: 1.1rem !important;
      }
      .msg-page-header small {
        font-size: 0.7rem !important;
      }
      .msg-compose-card .card-body {
        padding: 14px !important;
      }
      .msg-emoji-sidebar {
        display: none !important;
      }
      .msg-bubble-content {
        max-width: 88% !important;
      }
      .msg-bubble-text {
        font-size: 0.85rem !important;
      }
      .msg-feed-scroll {
        max-height: 48vh !important;
      }
    }

    /* ── Medium Mobile (360-413px) - iPhone X/11/12/13/14, Galaxy S ── */
    @media (min-width: 360px) and (max-width: 413px) {
      .msg-page {
        padding: 12px !important;
      }
      .msg-page-header h2 {
        font-size: 1.15rem !important;
      }
      .msg-compose-card .card-body {
        padding: 16px !important;
      }
      .msg-emoji-sidebar .btn {
        padding: 4px 8px !important;
        font-size: 0.9rem !important;
      }
      .msg-bubble-content {
        max-width: 85% !important;
      }
      .msg-feed-scroll {
        max-height: 50vh !important;
      }
    }

    /* ── Large Mobile (414-767px) - iPhone Plus/Max, Large Androids ── */
    @media (min-width: 414px) and (max-width: 767px) {
      .msg-page {
        padding: 14px !important;
      }
      .msg-page-header h2 {
        font-size: 1.25rem !important;
      }
      .msg-bubble-content {
        max-width: 80% !important;
      }
      .msg-feed-scroll {
        max-height: 52vh !important;
      }
    }

    /* ── ALL Mobile (< 768px) ── */
    @media (max-width: 767px) {
      .msg-page {
        padding-top: calc(10px + env(safe-area-inset-top, 0px)) !important;
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
        padding-left: calc(10px + env(safe-area-inset-left, 0px)) !important;
        padding-right: calc(10px + env(safe-area-inset-right, 0px)) !important;
      }

      .msg-page-header {
        margin-bottom: 1rem !important;
      }

      .msg-page-header .d-flex {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 10px !important;
      }

      .msg-mobile-toggle-bar {
        display: flex !important;
      }

      .msg-compose-card {
        border-radius: 1rem !important;
      }

      .msg-compose-card .card-header {
        border-radius: 1rem 1rem 0 0 !important;
        padding: 12px 16px !important;
      }

      .msg-compose-card .card-header h5 {
        font-size: 0.95rem !important;
      }

      .msg-feed-card {
        border-radius: 1rem !important;
      }

      .msg-feed-card .card-header {
        border-radius: 1rem 1rem 0 0 !important;
        padding: 12px 16px !important;
        flex-direction: column !important;
        gap: 8px !important;
      }

      .msg-feed-card .card-header h5 {
        font-size: 0.95rem !important;
      }

      .msg-filter-group {
        max-width: 100% !important;
        width: 100% !important;
      }

      .msg-feed-scroll {
        max-height: 55vh !important;
      }

      .msg-feed-messages {
        padding: 12px !important;
        gap: 10px !important;
      }

      .msg-card:hover {
        transform: none !important;
        box-shadow: 0 4px 15px rgba(0,0,0,0.2) !important;
      }

      .msg-submit-btn {
        padding: 14px !important;
        font-size: 1rem !important;
        border-radius: 12px !important;
      }

      .msg-refresh-btn {
        width: 100% !important;
      }
    }

    /* ── Mobile Landscape ── */
    @media (max-width: 767px) and (orientation: landscape) {
      .msg-page {
        padding-left: calc(14px + env(safe-area-inset-left, 0px)) !important;
        padding-right: calc(14px + env(safe-area-inset-right, 0px)) !important;
      }

      .msg-mobile-layout {
        flex-direction: row !important;
      }

      .msg-compose-section,
      .msg-feed-section {
        width: 50% !important;
        max-width: 50% !important;
        flex: 0 0 50% !important;
      }

      .msg-feed-scroll {
        max-height: 55vh !important;
      }

      .msg-compose-card textarea {
        min-height: 50px !important;
        rows: 2 !important;
      }

      .msg-page-header {
        margin-bottom: 0.5rem !important;
      }

      .msg-page-header h2 {
        font-size: 1rem !important;
      }
    }

    /* ── Tablet Portrait (768-1023px) - iPad Mini, iPad, Galaxy Tab ── */
    @media (min-width: 768px) and (max-width: 1023px) {
      .msg-page {
        padding: 1.5rem !important;
      }
      .msg-page-header h2 {
        font-size: 1.4rem !important;
      }
      .msg-bubble-content {
        max-width: 75% !important;
      }
      .msg-feed-scroll {
        max-height: 60vh !important;
      }
      .msg-compose-col {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        margin-bottom: 1rem !important;
      }
      .msg-feed-col {
        flex: 0 0 100% !important;
        max-width: 100% !important;
      }
    }

    /* ── Tablet Landscape (1024-1199px) - iPad Pro, Surface ── */
    @media (min-width: 1024px) and (max-width: 1199px) {
      .msg-page {
        padding: 1.5rem 2rem !important;
      }
      .msg-compose-col {
        flex: 0 0 35% !important;
        max-width: 35% !important;
      }
      .msg-feed-col {
        flex: 0 0 65% !important;
        max-width: 65% !important;
      }
      .msg-bubble-content {
        max-width: 72% !important;
      }
      .msg-feed-scroll {
        max-height: 65vh !important;
      }
    }

    /* ── Desktop (1200-1399px) ── */
    @media (min-width: 1200px) and (max-width: 1399px) {
      .msg-page {
        padding: 2rem !important;
      }
      .msg-bubble-content {
        max-width: 70% !important;
      }
      .msg-feed-scroll {
        max-height: 680px !important;
      }
    }

    /* ── Large Desktop (1400px+) ── */
    @media (min-width: 1400px) {
      .msg-page {
        padding: 2rem 3rem !important;
        max-width: 1600px;
        margin: 0 auto;
      }
      .msg-feed-scroll {
        max-height: 720px !important;
      }
    }

    /* ── Ultra Wide (1920px+) ── */
    @media (min-width: 1920px) {
      .msg-page {
        max-width: 1800px;
        padding: 2.5rem 4rem !important;
      }
      .msg-bubble-content {
        max-width: 60% !important;
      }
    }

    /* ── iPad-specific with Retina ── */
    @media (min-width: 768px) and (max-width: 1024px)
      and (-webkit-min-device-pixel-ratio: 2) {
      .msg-page {
        min-height: 100vh;
        min-height: 100dvh;
      }
    }

    /* ── High DPI Retina ── */
    @media (-webkit-min-device-pixel-ratio: 3),
           (min-resolution: 3dppx) {
      .msg-bubble-content {
        border: 0.33px solid rgba(255,255,255,0.05);
      }
    }

    /* ── Hover-capable devices only ── */
    @media (hover: hover) and (pointer: fine) {
      .msg-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 35px rgba(0,0,0,0.3) !important;
      }
      .msg-emoji-btn:hover {
        transform: scale(1.2);
        background: rgba(99,102,241,0.3) !important;
      }
      .msg-conv-item:hover {
        background: rgba(99,102,241,0.1);
      }
    }

    /* ── Touch-only active states ── */
    @media (hover: none) {
      .msg-card:active {
        transform: scale(0.99);
      }
      .msg-submit-btn:active:not(:disabled) {
        transform: scale(0.97);
      }
      .msg-emoji-btn:active {
        transform: scale(0.9);
        background: rgba(99,102,241,0.3) !important;
      }
    }

    /* ── Reduced Motion ── */
    @media (prefers-reduced-motion: reduce) {
      .msg-bubble-anim,
      .msg-alert-anim {
        animation: none !important;
      }
      .msg-card {
        transition: none !important;
      }
    }

    /* ── Dark Mode System ── */
    @media (prefers-color-scheme: dark) {
      .msg-page {
        color-scheme: dark;
      }
    }

    /* ── Print ── */
    @media print {
      .msg-compose-card,
      .msg-mobile-toggle-bar,
      .msg-page-header .btn,
      .msg-emoji-sidebar {
        display: none !important;
      }
      .msg-page {
        background: white !important;
        color: black !important;
      }
      .msg-bubble-content {
        border: 1px solid #ccc !important;
        color: black !important;
        background: #f5f5f5 !important;
      }
    }
  `}</style>
);

// ═══════════════════════════════════════════════════
// Main Message Component
// ═══════════════════════════════════════════════════
export default function Message() {
  const [messageText, setMessageText] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selectedSupId, setSelectedSupId] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ show: false, type: "", msg: "" });

  // Mobile view toggle: "compose" | "feed"
  const [mobileView, setMobileView] = useState("feed");

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const screen = useResponsive();

  const theme = {
    bg: "#0f172a",
    card: "#1e293b",
    text: "#f1f5f9",
    muted: "#94a3b8",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    border: "#334155",
    chatBubbleSent: "#6366f1",
    chatBubbleReceived: "#2d3748",
    headerBg: "#253549",
  };

  // ─── Responsive computed values ─────────────────
  const rv = {
    pagePad: screen.isFoldable ? "8px"
      : screen.isSmallMobile ? "10px"
      : screen.isMobile ? "14px"
      : screen.isTablet ? "1.5rem"
      : "2rem",

    headerSize: screen.isFoldable ? "1rem"
      : screen.isSmallMobile ? "1.1rem"
      : screen.isMobile ? "1.2rem"
      : screen.isTablet ? "1.4rem"
      : "1.5rem",

    subtitleSize: screen.isFoldable ? "0.65rem"
      : screen.isMobile ? "0.75rem"
      : "0.85rem",

    bubbleMaxW: screen.isFoldable ? "92%"
      : screen.isSmallMobile ? "88%"
      : screen.isMobile ? "82%"
      : screen.isTablet ? "75%"
      : "70%",

    bubblePad: screen.isFoldable ? "8px 10px"
      : screen.isMobile ? "10px 12px"
      : "12px 16px",

    bubbleFontSize: screen.isFoldable ? "0.8rem"
      : screen.isSmallMobile ? "0.85rem"
      : "0.9rem",

    avatarSize: screen.isFoldable ? 24
      : screen.isSmallMobile ? 28
      : screen.isMobile ? 30
      : 32,

    textareaRows: screen.isFoldable ? 3
      : screen.isMobile && screen.isLandscape ? 2
      : screen.isMobile ? 4
      : 6,

    feedMaxH: screen.isFoldable ? "45vh"
      : screen.isSmallMobile ? "48vh"
      : screen.isMobile && screen.isLandscape ? "55vh"
      : screen.isMobile ? "55vh"
      : screen.isTabletPortrait ? "60vh"
      : screen.isTabletLandscape ? "65vh"
      : "680px",

    showEmoji: !screen.isFoldable && !screen.isSmallMobile,

    cardRadius: screen.isMobile ? "1rem" : "1.25rem",

    submitPad: screen.isMobile ? "12px" : "14px",

    filterWidth: screen.isFoldable ? "120px"
      : screen.isMobile ? "100%"
      : "280px",
  };

  // ─── Fetch Logic ────────────────────────────────
  const fetchMessages = useCallback(async (userId) => {
    try {
      setLoading(true);
      const params = userId ? { user_id: userId } : {};
      const res = await axios.get(API_URL1, { params });
      const sorted = res.data ? res.data.sort((a, b) => b.id - a.id) : [];
      setMessages(sorted);
    } catch (err) {
      console.error("Fetch error:", err);
      showToast("Ikibazo mu gushaka ubutumwa.", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const res = await axios.get(API_URL2);
        if (res.data) setRecipients(res.data);
      } catch (err) {
        console.error("Error fetching recipients:", err);
      }
    };
    fetchRecipients();
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const showToast = (msg, type) => {
    setStatus({ show: true, type, msg });
    setTimeout(() => setStatus({ show: false, type: "", msg: "" }), 3200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupId) {
      showToast("Banza uhitemo uwo woherereza.", "danger");
      return;
    }
    if (!messageText.trim()) {
      showToast("Andika ubutumwa mbere yo kohereza.", "warning");
      return;
    }

    setSending(true);

    try {
      await axios.post(API_URL, {
        text_user: messageText,
        sentId: selectedSupId,
      });

      setMessageText("");
      setSelectedSupId("");
      fetchMessages(filterUserId);
      showToast("Ubutumwa bwoherejwe neza! 🎉", "success");

      // Switch to feed view on mobile after sending
      if (screen.isMobile) {
        setMobileView("feed");
      }
    } catch (err) {
      console.error("Save error:", err);
      showToast("Byanze kohereza. Gerageza ukundi.", "danger");
    } finally {
      setSending(false);
    }
  };

  const addEmoji = (emoji) => {
    setMessageText((prev) => prev + " " + emoji);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // ─── RENDER ─────────────────────────────────────
  return (
    <>
      <ResponsiveStyles theme={theme} />

      <div
        className="msg-page"
        style={{
          background: theme.bg,
          color: theme.text,
          minHeight: "100vh",
          padding: rv.pagePad,
        }}
      >
        {/* ═══ HEADER ═══ */}
        <div
          className="msg-page-header"
          style={{ marginBottom: screen.isMobile ? "1rem" : "2rem" }}
        >
          <div
            className="d-flex justify-content-between align-items-center flex-wrap gap-3"
            style={{
              flexDirection: screen.isMobile ? "column" : "row",
              alignItems: screen.isMobile ? "flex-start" : "center",
            }}
          >
            <div>
              <h2
                className="fw-bold mb-1"
                style={{
                  color: theme.primary,
                  fontSize: rv.headerSize,
                  lineHeight: 1.3,
                }}
              >
                <i className="bi bi-chat-dots-fill me-2"></i>
                Ibitekerezo n'Ubutumwa
              </h2>
              <small
                style={{
                  color: theme.muted,
                  fontSize: rv.subtitleSize,
                }}
              >
                Tanga ibitekerezo cyangwa ubutumwa bwihuse ku muyobozi
              </small>
            </div>

            <Button
              className="msg-refresh-btn msg-touch-target"
              style={{
                background: theme.primary,
                border: "none",
                width: screen.isMobile ? "100%" : "auto",
                minHeight: "44px",
                fontSize: screen.isMobile ? "0.9rem" : "1rem",
              }}
              onClick={() => fetchMessages(filterUserId)}
              disabled={loading}
            >
              <i
                className={`bi bi-arrow-clockwise ${
                  loading ? "spinner-border-sm" : ""
                } me-2`}
              ></i>
              Refresh
            </Button>
          </div>
        </div>

        {/* ═══ TOAST NOTIFICATION ═══ */}
        {status.show && (
          <Alert
            variant={status.type}
            dismissible
            onClose={() => setStatus({ show: false })}
            className="msg-alert-anim mb-3 shadow"
            style={{
              borderRadius: "12px",
              fontSize: screen.isMobile ? "0.85rem" : "0.95rem",
            }}
          >
            <i
              className={`bi bi-${
                status.type === "success"
                  ? "check-circle-fill"
                  : status.type === "warning"
                  ? "exclamation-triangle-fill"
                  : "exclamation-triangle-fill"
              } me-2`}
            ></i>
            {status.msg}
          </Alert>
        )}

        {/* ═══ MOBILE VIEW TOGGLE ═══ */}
        {screen.isMobile && (
          <div
            className="msg-mobile-toggle-bar d-flex gap-2 mb-3"
            style={{ display: "flex" }}
          >
            <Button
              className="msg-touch-target flex-fill"
              style={{
                background:
                  mobileView === "compose"
                    ? theme.primary
                    : "rgba(99,102,241,0.15)",
                color:
                  mobileView === "compose" ? "#fff" : theme.primary,
                border: `1px solid ${
                  mobileView === "compose" ? theme.primary : theme.border
                }`,
                borderRadius: "12px",
                fontWeight: "600",
                fontSize: "0.9rem",
                padding: "10px",
              }}
              onClick={() => setMobileView("compose")}
            >
              <i className="bi bi-pencil-square me-1"></i>
              Andika
            </Button>
            <Button
              className="msg-touch-target flex-fill"
              style={{
                background:
                  mobileView === "feed"
                    ? theme.primary
                    : "rgba(99,102,241,0.15)",
                color:
                  mobileView === "feed" ? "#fff" : theme.primary,
                border: `1px solid ${
                  mobileView === "feed" ? theme.primary : theme.border
                }`,
                borderRadius: "12px",
                fontWeight: "600",
                fontSize: "0.9rem",
                padding: "10px",
                position: "relative",
              }}
              onClick={() => setMobileView("feed")}
            >
              <i className="bi bi-chat-left-text me-1"></i>
              Ubutumwa
              {messages.length > 0 && (
                <Badge
                  bg="danger"
                  pill
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-6px",
                    fontSize: "0.65rem",
                  }}
                >
                  {messages.length}
                </Badge>
              )}
            </Button>
          </div>
        )}

        {/* ═══ MAIN LAYOUT ═══ */}
        <Row
          className={`g-3 g-lg-4 msg-mobile-layout ${
            screen.isMobile && screen.isLandscape ? "flex-row" : ""
          }`}
        >
          {/* ─── COMPOSE CARD ─── */}
          <Col
            xs={12}
            md={screen.isTabletPortrait ? 12 : 4}
            lg={4}
            className={`msg-compose-col msg-compose-section ${
              screen.isMobile && mobileView !== "compose" ? "d-none" : ""
            }`}
          >
            <Card
              className="msg-card msg-compose-card border-0 shadow"
              style={{
                background: theme.card,
                borderRadius: rv.cardRadius,
                overflow: "hidden",
              }}
            >
              <Card.Header
                style={{
                  background: theme.primary,
                  color: "white",
                  border: "none",
                  padding: screen.isMobile ? "12px 16px" : "16px 20px",
                }}
              >
                <h5
                  className="mb-0 d-flex align-items-center"
                  style={{
                    fontSize: screen.isMobile ? "0.95rem" : "1.1rem",
                  }}
                >
                  <i className="bi bi-pencil-square me-2"></i>
                  Andika Hano
                </h5>
              </Card.Header>

              <Card.Body
                style={{
                  padding: screen.isFoldable
                    ? "12px"
                    : screen.isMobile
                    ? "16px"
                    : "20px",
                }}
              >
                <Form onSubmit={handleSubmit}>
                  {/* Recipient Select */}
                  <Form.Group className="mb-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        fontSize: screen.isMobile ? "0.85rem" : "0.9rem",
                        color: theme.text,
                      }}
                    >
                      <i
                        className="bi bi-person-fill me-1"
                        style={{ color: theme.primary }}
                      ></i>
                      Uwo woherereza *
                    </Form.Label>
                    <Form.Select
                      className="msg-input-fix msg-touch-target"
                      value={selectedSupId}
                      onChange={(e) => setSelectedSupId(e.target.value)}
                      style={{
                        background: theme.bg,
                        color: theme.text,
                        borderColor: theme.border,
                        borderRadius: "10px",
                        padding: "10px 12px",
                        minHeight: "44px",
                      }}
                      required
                    >
                      <option value="">Hitamo uwo woherereza...</option>
                      {recipients.map((person) => (
                        <option key={person.user_id} value={person.user_id}>
                          {person.fullname || `Umuyobozi ${person.user_id}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* Message Textarea + Emoji */}
                  <Form.Group className="mb-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        fontSize: screen.isMobile ? "0.85rem" : "0.9rem",
                        color: theme.text,
                      }}
                    >
                      <i
                        className="bi bi-chat-text-fill me-1"
                        style={{ color: theme.primary }}
                      ></i>
                      Ubutumwa bwawe *
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        ref={textareaRef}
                        as="textarea"
                        rows={rv.textareaRows}
                        className="msg-input-fix"
                        placeholder="Andika hano... 💬😊"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        style={{
                          background: theme.bg,
                          color: theme.text,
                          borderColor: theme.border,
                          borderRadius: "12px",
                          resize: "none",
                          lineHeight: 1.5,
                          padding: "12px",
                          minHeight: screen.isFoldable
                            ? "60px"
                            : screen.isMobile
                            ? "80px"
                            : "120px",
                        }}
                        required
                      />

                      {/* Emoji Sidebar - hidden on very small screens */}
                      {rv.showEmoji && (
                        <div
                          className="msg-emoji-sidebar d-flex flex-column gap-1"
                          style={{ flexShrink: 0 }}
                        >
                          {["😊", "👍", "❤️", "🙏", "👏", "🎉"].map(
                            (emoji) => (
                              <Button
                                key={emoji}
                                className="msg-emoji-btn msg-touch-target"
                                variant="outline-light"
                                size="sm"
                                style={{
                                  borderColor: theme.border,
                                  borderRadius: "8px",
                                  padding: screen.isMobile
                                    ? "4px 8px"
                                    : "6px 10px",
                                  fontSize: screen.isMobile
                                    ? "0.9rem"
                                    : "1rem",
                                  transition: "all 0.15s",
                                  minWidth: "36px",
                                  minHeight: "36px",
                                }}
                                onClick={() => addEmoji(emoji)}
                                title={`Add ${emoji}`}
                              >
                                {emoji}
                              </Button>
                            )
                          )}
                        </div>
                      )}
                    </div>

                    {/* Character count */}
                    <small
                      style={{
                        color:
                          messageText.length > 500 ? theme.warning : theme.muted,
                        fontSize: "0.7rem",
                        display: "block",
                        textAlign: "right",
                        marginTop: "4px",
                      }}
                    >
                      {messageText.length} / 1000
                    </small>
                  </Form.Group>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="msg-submit-btn msg-touch-target w-100 fw-bold"
                    style={{
                      background: theme.success,
                      border: "none",
                      padding: rv.submitPad,
                      borderRadius: "12px",
                      fontSize: screen.isMobile ? "0.95rem" : "1rem",
                      minHeight: "48px",
                      opacity:
                        sending || !messageText.trim() || !selectedSupId
                          ? 0.6
                          : 1,
                      transition: "all 0.2s",
                    }}
                    disabled={sending || !messageText.trim() || !selectedSupId}
                  >
                    {sending ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Birimo kohereza...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send-fill me-2"></i>
                        Kohereza
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* ─── MESSAGE FEED ─── */}
          <Col
            xs={12}
            md={screen.isTabletPortrait ? 12 : 8}
            lg={8}
            className={`msg-feed-col msg-feed-section ${
              screen.isMobile && mobileView !== "feed" ? "d-none" : ""
            }`}
          >
            <Card
              className="msg-card msg-feed-card border-0 shadow"
              style={{
                background: theme.card,
                borderRadius: rv.cardRadius,
                overflow: "hidden",
              }}
            >
              {/* Feed Header */}
              <Card.Header
                style={{
                  background: theme.headerBg,
                  color: theme.text,
                  borderBottom: `1px solid ${theme.border}`,
                  padding: screen.isMobile ? "12px 16px" : "16px 20px",
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                  style={{
                    flexDirection: screen.isMobile ? "column" : "row",
                    alignItems: screen.isMobile ? "stretch" : "center",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: screen.isMobile ? "0.95rem" : "1.1rem",
                    }}
                  >
                    <i className="bi bi-chat-left-text me-2"></i>
                    Ubutumwa Buheruka
                    {filterUserId && (
                      <Badge bg="primary" className="ms-2" style={{ fontSize: "0.7rem" }}>
                        User #{filterUserId}
                      </Badge>
                    )}
                  </h5>

                  <InputGroup
                    className="msg-filter-group"
                    style={{
                      maxWidth: rv.filterWidth,
                      width: screen.isMobile ? "100%" : "auto",
                    }}
                  >
                    <InputGroup.Text
                      style={{
                        background: theme.bg,
                        borderColor: theme.border,
                        color: theme.muted,
                        borderRadius: "10px 0 0 10px",
                      }}
                    >
                      <i className="bi bi-filter"></i>
                    </InputGroup.Text>
                    <Form.Control
                      className="msg-input-fix"
                      type="number"
                      placeholder="Filter User ID"
                      value={filterUserId}
                      onChange={(e) => {
                        setFilterUserId(e.target.value);
                        fetchMessages(e.target.value);
                      }}
                      style={{
                        background: theme.bg,
                        color: theme.text,
                        borderColor: theme.border,
                        borderRadius: "0 10px 10px 0",
                        minHeight: "40px",
                      }}
                    />
                  </InputGroup>
                </div>
              </Card.Header>

              {/* Messages Area */}
              <Card.Body className="p-0">
                <div
                  className="overflow-auto msg-custom-scroll msg-feed-scroll"
                  style={{
                    maxHeight: rv.feedMaxH,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {loading ? (
                    <div className="text-center py-5 my-4">
                      <Spinner
                        animation="border"
                        style={{
                          color: theme.primary,
                          width: screen.isMobile ? "2.5rem" : "3.5rem",
                          height: screen.isMobile ? "2.5rem" : "3.5rem",
                        }}
                      />
                      <p
                        className="mt-3"
                        style={{
                          color: theme.muted,
                          fontSize: screen.isMobile ? "0.85rem" : "0.95rem",
                        }}
                      >
                        Ibyo birimo gutegurwa...
                      </p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-5 my-4">
                      <i
                        className="bi bi-chat-square-dots"
                        style={{
                          fontSize: screen.isMobile ? "3.5rem" : "5rem",
                          color: theme.border,
                        }}
                      ></i>
                      <h5
                        className="mt-3"
                        style={{
                          color: theme.muted,
                          fontSize: screen.isMobile ? "1rem" : "1.15rem",
                        }}
                      >
                        Nta butumwa buraboneka
                      </h5>
                      <p
                        className="small"
                        style={{
                          color: theme.muted,
                          fontSize: screen.isMobile ? "0.75rem" : "0.85rem",
                        }}
                      >
                        Ba uwa mbere mu gutanga igitekerezo!
                      </p>
                    </div>
                  ) : (
                    <div
                      className="d-flex flex-column msg-feed-messages"
                      style={{
                        padding: screen.isMobile ? "12px" : "20px",
                        gap: screen.isMobile ? "10px" : "14px",
                      }}
                    >
                      {messages.map((m) => (
                        <div
                          key={m.id}
                          className="msg-bubble-anim d-flex flex-column"
                          style={{
                            alignItems: m.sent_by_current_user
                              ? "flex-end"
                              : "flex-start",
                          }}
                        >
                          <div
                            className="msg-bubble-content shadow-sm"
                            style={{
                              maxWidth: rv.bubbleMaxW,
                              padding: rv.bubblePad,
                              background: m.sent_by_current_user
                                ? theme.chatBubbleSent
                                : theme.chatBubbleReceived,
                              color: m.sent_by_current_user
                                ? "white"
                                : theme.text,
                              borderRadius: m.sent_by_current_user
                                ? "18px 18px 0 18px"
                                : "18px 18px 18px 0",
                              wordBreak: "break-word",
                            }}
                          >
                            {/* Sender Info */}
                            <div
                              className="d-flex align-items-center gap-2 mb-2"
                              style={{
                                flexWrap: "nowrap",
                              }}
                            >
                              <div
                                className="msg-bubble-avatar rounded-circle d-flex align-items-center justify-content-center fw-bold"
                                style={{
                                  width: rv.avatarSize,
                                  height: rv.avatarSize,
                                  minWidth: rv.avatarSize,
                                  background: m.sent_by_current_user
                                    ? "rgba(255,255,255,0.2)"
                                    : theme.primary,
                                  color: "white",
                                  fontSize: `${rv.avatarSize * 0.45}px`,
                                  flexShrink: 0,
                                }}
                              >
                                {m.user_id
                                  ? m.user_id.toString().charAt(0)
                                  : "?"}
                              </div>
                              <small
                                style={{
                                  opacity: 0.8,
                                  fontWeight: 500,
                                  fontSize: screen.isFoldable
                                    ? "0.65rem"
                                    : screen.isMobile
                                    ? "0.75rem"
                                    : "0.8rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {m.user_id
                                  ? `User #${m.user_id}`
                                  : "Anonymous"}
                              </small>
                            </div>

                            {/* Message Text */}
                            <p
                              className="msg-bubble-text mb-2"
                              style={{
                                lineHeight: 1.5,
                                whiteSpace: "pre-wrap",
                                fontSize: rv.bubbleFontSize,
                                margin: 0,
                                marginBottom: "6px",
                              }}
                            >
                              {m.text_user}
                            </p>

                            {/* Time */}
                            <small
                              style={{
                                opacity: 0.7,
                                display: "block",
                                textAlign: "right",
                                fontSize: screen.isFoldable
                                  ? "0.6rem"
                                  : "0.7rem",
                              }}
                            >
                              {m.created_at
                                ? new Date(m.created_at).toLocaleTimeString(
                                    "rw-RW",
                                    { hour: "2-digit", minute: "2-digit" }
                                  )
                                : new Date().toLocaleTimeString("rw-RW", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                              {m.sent_by_current_user && (
                                <span className="ms-1" style={{ fontSize: "0.65rem" }}>
                                  ✓✓
                                </span>
                              )}
                            </small>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
              </Card.Body>

              {/* Feed Footer */}
              <Card.Footer
                style={{
                  background: theme.headerBg,
                  borderTop: `1px solid ${theme.border}`,
                  padding: screen.isMobile ? "10px 16px" : "12px 20px",
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{ flexWrap: "wrap", gap: "6px" }}
                >
                  <small
                    style={{
                      color: theme.muted,
                      fontSize: screen.isMobile ? "0.7rem" : "0.8rem",
                    }}
                  >
                    <i className="bi bi-info-circle me-1"></i>
                    Ubutumwa: <strong>{messages.length}</strong>
                  </small>

                  {/* Quick compose on mobile */}
                  {screen.isMobile && mobileView === "feed" && (
                    <Button
                      className="msg-touch-target"
                      size="sm"
                      style={{
                        background: theme.success,
                        border: "none",
                        borderRadius: "20px",
                        padding: "6px 14px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                      }}
                      onClick={() => setMobileView("compose")}
                    >
                      <i className="bi bi-pencil-fill me-1"></i>
                      Andika
                    </Button>
                  )}
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* ═══ MOBILE FAB (Floating Action Button) ═══ */}
        {screen.isMobile && mobileView === "feed" && (
          <button
            className="msg-touch-target"
            onClick={() => setMobileView("compose")}
            style={{
              position: "fixed",
              bottom: `calc(20px + env(safe-area-inset-bottom, 0px))`,
              right: "20px",
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
              color: "#fff",
              border: "none",
              boxShadow: "0 6px 20px rgba(99,102,241,0.4)",
              fontSize: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title="Andika ubutumwa bushya"
          >
            <i className="bi bi-pencil-fill"></i>
          </button>
        )}

        {/* ═══ MOBILE BACK BUTTON (when composing) ═══ */}
        {screen.isMobile && mobileView === "compose" && (
          <button
            className="msg-touch-target"
            onClick={() => setMobileView("feed")}
            style={{
              position: "fixed",
              bottom: `calc(20px + env(safe-area-inset-bottom, 0px))`,
              right: "20px",
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.warning}, #d97706)`,
              color: "#fff",
              border: "none",
              boxShadow: "0 6px 20px rgba(245,158,11,0.4)",
              fontSize: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title="Reba ubutumwa"
          >
            <i className="bi bi-chat-left-text-fill"></i>
          </button>
        )}
      </div>
    </>
  );
}